import { useCallback, useEffect, useRef } from "react"
import { meetings } from "services/api/meetingsClient"
import type { MeetingResponse } from "services/api/meetingsClient"
import { requiresMeetingAdmission, type JoinInfo } from "../types"
import { getPanelTokenFromUrl } from "../shared/panelUrls"

type UseMeetingAttendanceRecorderArgs = {
  meetingId?: string
  joinInfo: JoinInfo | null
  meeting: MeetingResponse | null
  effectiveIsAuthenticated: boolean
}

const MESSAGES_PRESENCE_INTERVAL_MS = 20000

/**
 * Attendance side effects shared by every renderer: authenticated join confirmation,
 * waiting-room admission recording, and the Messages presence heartbeat. Each fires at
 * most once per join; surfaces supply the Daily participant id when events happen.
 */
export function useMeetingAttendanceRecorder({
  meetingId,
  joinInfo,
  meeting,
  effectiveIsAuthenticated,
}: UseMeetingAttendanceRecorderArgs) {
  const latestRef = useRef({ meetingId, joinInfo, meeting, effectiveIsAuthenticated })
  latestRef.current = { meetingId, joinInfo, meeting, effectiveIsAuthenticated }
  const waitRoomAdmissionRecordedRef = useRef(false)
  const joinConfirmationRecordedRef = useRef(false)
  const presenceIntervalRef = useRef<number | null>(null)
  const presenceStartedRef = useRef(false)

  const recordAdmission = useCallback(async (participantId: string) => {
    const { meetingId: id, joinInfo: info, effectiveIsAuthenticated: authenticated } = latestRef.current
    if (
      waitRoomAdmissionRecordedRef.current ||
      !requiresMeetingAdmission(info) ||
      !id ||
      !authenticated
    ) {
      return
    }
    waitRoomAdmissionRecordedRef.current = true
    try {
      await meetings.recordWaitRoomAdmission(id, {
        display_name: info?.display_name,
        daily_participant_id: participantId ? String(participantId) : undefined,
      })
    } catch {
      // Attendance reconciliation should not eject someone Daily has already admitted.
    }
  }, [])

  const confirmJoin = useCallback(async (participantId: string) => {
    const { meetingId: id, joinInfo: info, meeting: current, effectiveIsAuthenticated: authenticated } = latestRef.current
    if (
      joinConfirmationRecordedRef.current ||
      !info?.token ||
      requiresMeetingAdmission(info) ||
      !id ||
      !authenticated ||
      !current
    ) {
      return
    }
    if (!participantId) return

    joinConfirmationRecordedRef.current = true
    try {
      await meetings.confirmMeetingJoin(id, {
        display_name: info.display_name,
        daily_participant_id: String(participantId),
      })
    } catch {
      joinConfirmationRecordedRef.current = false
      // Attendance confirmation is best-effort and should not interrupt an active call.
    }
  }, [])

  const stopPresenceHeartbeat = useCallback(() => {
    if (presenceIntervalRef.current) {
      window.clearInterval(presenceIntervalRef.current)
      presenceIntervalRef.current = null
    }
    presenceStartedRef.current = false
  }, [])

  const startPresenceHeartbeat = useCallback((getParticipantId: () => string) => {
    if (presenceStartedRef.current) return
    presenceStartedRef.current = true
    const updatePresence = async () => {
      const { meetingId: id, joinInfo: info } = latestRef.current
      if (!id || !info?.messages_panel?.enabled) return
      const panelToken = getPanelTokenFromUrl(info.messages_panel.url)
      if (!panelToken) return
      const participantId = getParticipantId()
      try {
        await meetings.updateMeetingMessagesPresence(id, panelToken, {
          daily_participant_id: participantId ? String(participantId) : undefined,
        })
      } catch {
        // Messaging presence is best-effort and should not interrupt the call.
      }
    }
    void updatePresence()
    if (presenceIntervalRef.current) {
      window.clearInterval(presenceIntervalRef.current)
    }
    presenceIntervalRef.current = window.setInterval(() => {
      void updatePresence()
    }, MESSAGES_PRESENCE_INTERVAL_MS)
  }, [])

  const reset = useCallback(() => {
    waitRoomAdmissionRecordedRef.current = false
    joinConfirmationRecordedRef.current = false
  }, [])

  useEffect(() => {
    return () => {
      if (presenceIntervalRef.current) {
        window.clearInterval(presenceIntervalRef.current)
        presenceIntervalRef.current = null
      }
      presenceStartedRef.current = false
    }
  }, [])

  return { recordAdmission, confirmJoin, startPresenceHeartbeat, stopPresenceHeartbeat, reset }
}
